import { useId, useState } from "react";
import type {
  CSSProperties,
  HTMLAttributes,
  ReactElement,
  ReactNode,
  SelectHTMLAttributes,
} from "react";
import {
  ArrowDownIcon,
  ArrowUpDownIcon,
  ArrowUpIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  CircleHelpIcon,
} from "lucide-react";

import { Popover, PopoverPopup, PopoverTrigger } from "~/components/ui/popover";
import { Tooltip, TooltipPopup, TooltipTrigger } from "~/components/ui/tooltip";
import { cn } from "~/lib/utils";

/** Native select/input chrome that matches Input / SelectTrigger visibility. */
export const dashboardControlClassName =
  "h-8 min-w-[12.5rem] rounded-lg border border-input bg-background px-2.5 text-sm text-foreground shadow-xs outline-none transition-[color,box-shadow,border-color] focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/24 disabled:opacity-64";

export function DashboardSelect({ className, ...props }: SelectHTMLAttributes<HTMLSelectElement>) {
  return <select className={cn(dashboardControlClassName, className)} {...props} />;
}

export function DashboardFilterField(props: {
  children: ReactNode;
  htmlFor?: string;
  label: string;
}) {
  return (
    <label htmlFor={props.htmlFor} className="grid gap-1 text-xs font-medium text-muted-foreground">
      {props.label}
      {props.children}
    </label>
  );
}

export function DashboardFilterRow(props: { children: ReactNode; trailing?: ReactNode }) {
  return (
    <div className="flex flex-wrap items-end gap-3">
      {props.children}
      {props.trailing ? (
        <div className="ml-auto self-center text-xs text-muted-foreground">{props.trailing}</div>
      ) : null}
    </div>
  );
}

export function DefinitionPopover(props: { definition: string; label: string }) {
  return (
    <Popover>
      <PopoverTrigger
        type="button"
        aria-label={`About ${props.label}`}
        className="inline-flex size-4 shrink-0 items-center justify-center rounded-full text-muted-foreground transition-colors hover:text-foreground"
      >
        <CircleHelpIcon className="size-3.5" aria-hidden />
      </PopoverTrigger>
      <PopoverPopup side="top" tooltipStyle className="max-w-72 text-xs leading-relaxed">
        {props.definition}
      </PopoverPopup>
    </Popover>
  );
}

function HeaderDefinitionTooltip(props: {
  children: ReactElement;
  definition: string | undefined;
}) {
  const descriptionId = useId();
  if (!props.definition) return props.children;
  return (
    <Tooltip>
      <TooltipTrigger render={props.children} aria-describedby={descriptionId} />
      <TooltipPopup id={descriptionId} role="tooltip" className="max-w-72 text-xs leading-relaxed">
        {props.definition}
      </TooltipPopup>
    </Tooltip>
  );
}

export function KpiCard(props: {
  action?: ReactNode;
  delta?: string | null | undefined;
  deltaDirection?: "up" | "down" | undefined;
  footnote?: ReactNode;
  label: string;
  tooltip?: string;
  value: ReactNode;
}) {
  return (
    <article className="rounded-lg border border-border/70 bg-background/80 px-4 py-3 shadow-xs">
      <div className="flex items-center gap-1 text-xs text-muted-foreground">
        <p>{props.label}</p>
        {props.tooltip ? (
          <DefinitionPopover definition={props.tooltip} label={props.label} />
        ) : null}
        {props.action ? <div className="ml-auto flex items-center">{props.action}</div> : null}
      </div>
      <div className="mt-1 text-2xl font-semibold tracking-tight text-foreground">
        {props.value}
      </div>
      {props.delta ? (
        <p
          className={cn(
            "mt-1 text-xs font-medium",
            props.deltaDirection === "down" ? "text-red-600" : "text-emerald-700",
          )}
        >
          {props.delta}
        </p>
      ) : null}
      {props.footnote ? (
        <p className="mt-1 text-xs text-muted-foreground">{props.footnote}</p>
      ) : null}
    </article>
  );
}

export function HorizonTabs(props: {
  idPrefix: string;
  onChange: (value: string) => void;
  options: Array<{ count?: string; label: string; value: string }>;
  value: string;
}) {
  return (
    <div className="flex flex-wrap gap-2 border-b border-border/70 pb-px" role="tablist">
      {props.options.map((option) => {
        const active = props.value === option.value;
        return (
          <button
            key={option.value}
            id={`${props.idPrefix}-tab-${option.value}`}
            type="button"
            role="tab"
            aria-selected={active}
            tabIndex={active ? 0 : -1}
            onKeyDown={(event) => {
              const index = props.options.indexOf(option);
              const next =
                event.key === "ArrowRight"
                  ? (index + 1) % props.options.length
                  : event.key === "ArrowLeft"
                    ? (index - 1 + props.options.length) % props.options.length
                    : event.key === "Home"
                      ? 0
                      : event.key === "End"
                        ? props.options.length - 1
                        : null;
              if (next === null) return;
              event.preventDefault();
              const target = props.options[next];
              if (!target) return;
              props.onChange(target.value);
              event.currentTarget.parentElement
                ?.querySelector<HTMLButtonElement>(`[id="${props.idPrefix}-tab-${target.value}"]`)
                ?.focus();
            }}
            onClick={() => props.onChange(option.value)}
            className={cn(
              "-mb-px border-b-2 px-3 py-2 text-sm font-medium transition-colors",
              active
                ? "border-foreground text-foreground"
                : "border-transparent text-muted-foreground hover:text-foreground",
            )}
          >
            {option.label}
            {option.count ? (
              <span className="ml-2 text-xs font-normal text-muted-foreground">{option.count}</span>
            ) : null}
          </button>
        );
      })}
    </div>
  );
}

export function KpiGrid(props: { children: ReactNode; className?: string }) {
  return (
    <section className={cn("grid grid-cols-2 gap-3 md:grid-cols-4", props.className)}>
      {props.children}
    </section>
  );
}

export function ChartPanel(props: { action?: ReactNode; children: ReactNode; title?: string }) {
  return (
    <article className="rounded-lg border border-border/70 bg-background/80 p-4 shadow-xs">
      {props.title || props.action ? (
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
          {props.title ? (
            <h3 className="text-sm font-semibold text-foreground">{props.title}</h3>
          ) : (
            <span />
          )}
          {props.action}
        </div>
      ) : null}
      {props.children}
    </article>
  );
}

export type DataTableColumn<T> = {
  align?: "left" | "right";
  definition?: string;
  key: string;
  label: string;
  render?: (row: T) => ReactNode;
  sortable?: boolean;
  sortValue?: (row: T) => number | string | null | undefined;
};

type DataTableSort = { direction: "asc" | "desc"; key: string };

function compareTableValues(
  left: number | string | null | undefined,
  right: number | string | null | undefined,
) {
  if (left == null && right == null) return 0;
  if (left == null) return 1;
  if (right == null) return -1;
  if (typeof left === "number" && typeof right === "number") return left - right;
  return String(left).localeCompare(String(right), undefined, {
    numeric: true,
    sensitivity: "base",
  });
}

export type DataTableServerSort = {
  column?: string;
  direction?: "asc" | "desc";
  onCycle: (column: string) => void;
};

export function DataTable<T extends object>(props: {
  /**
   * Rows nested under a row (e.g. a provider's sites), hidden until the row is expanded and
   * sorted like their parents. Return nothing for a plain row.
   */
  childRows?: (row: T) => T[] | undefined;
  columns: Array<DataTableColumn<T>>;
  defaultSort?: DataTableSort;
  emptyMessage?: string;
  /** Optional max height for in-table vertical scroll. Off by default. */
  maxHeightClass?: string;
  pinRow?: (row: T) => "bottom" | "top" | undefined;
  rowKey: (row: T, index: number) => string;
  rows: T[];
  /** When set, header clicks ask the caller to sort. Rows stay in server order. */
  serverSort?: DataTableServerSort;
}) {
  const [sort, setSort] = useState<DataTableSort | undefined>(props.defaultSort);
  const manual = props.serverSort;
  const activeSort: DataTableSort | undefined = manual
    ? manual.column && manual.direction
      ? { direction: manual.direction, key: manual.column }
      : undefined
    : sort;
  const [expanded, setExpanded] = useState<ReadonlySet<string>>(() => new Set());
  const sortRows = (rows: T[]) => {
    if (manual || !activeSort) return rows;
    const column = props.columns.find((item) => item.key === activeSort.key);
    if (!column?.sortable) return rows;
    const getValue =
      column.sortValue || ((row: T) => (row as Record<string, unknown>)[column.key] as string);
    const multiplier = activeSort.direction === "asc" ? 1 : -1;
    return rows
      .map((row, index) => ({ index, pin: props.pinRow?.(row), row }))
      .sort((left, right) => {
        if (left.pin !== right.pin) {
          if (left.pin === "top" || right.pin === "bottom") return -1;
          if (left.pin === "bottom" || right.pin === "top") return 1;
        }
        const compared = compareTableValues(getValue(left.row), getValue(right.row));
        return compared === 0 ? left.index - right.index : compared * multiplier;
      })
      .map(({ row }) => row);
  };
  const toggle = (key: string) =>
    setExpanded((current) => {
      const next = new Set(current);
      if (!next.delete(key)) next.add(key);
      return next;
    });
  const renderRow = (row: T, key: string, depth: 0 | 1, children?: T[]) => (
    <tr key={key} className={cn("border-b border-border/40 last:border-0", depth && "bg-muted/30")}>
      {props.columns.map((col, columnIndex) => {
        const record = row as Record<string, unknown>;
        const content = col.render ? col.render(row) : String(record[col.key] ?? "");
        return (
          <td
            key={col.key}
            className={cn(
              "px-2 py-2 text-foreground",
              col.align === "right" && "text-right",
              columnIndex === 0 && depth && "pl-8",
            )}
          >
            {columnIndex === 0 && children?.length ? (
              <button
                type="button"
                className="inline-flex items-center gap-1 text-left font-semibold hover:underline"
                aria-expanded={expanded.has(key)}
                onClick={() => toggle(key)}
              >
                {expanded.has(key) ? (
                  <ChevronDownIcon className="size-3.5 shrink-0" aria-hidden />
                ) : (
                  <ChevronRightIcon className="size-3.5 shrink-0" aria-hidden />
                )}
                {content}
                <span className="text-xs font-normal text-muted-foreground">
                  ({children.length})
                </span>
              </button>
            ) : (
              content
            )}
          </td>
        );
      })}
    </tr>
  );

  if (!props.rows.length && !manual) {
    return (
      <p className="text-sm text-muted-foreground">{props.emptyMessage || "No rows to display."}</p>
    );
  }

  return (
    <div
      className={cn(
        "overflow-x-auto",
        props.maxHeightClass && "overflow-y-auto",
        props.maxHeightClass,
      )}
    >
      <table className="w-full min-w-[240px] border-collapse text-sm">
        <thead>
          <tr className="border-b border-border/70 text-left text-xs text-muted-foreground">
            {props.columns.map((col) => (
              <th
                key={col.key}
                aria-sort={
                  activeSort?.key === col.key
                    ? activeSort.direction === "asc"
                      ? "ascending"
                      : "descending"
                    : undefined
                }
                className={cn(
                  "bg-background px-2 py-2 font-medium",
                  props.maxHeightClass && "sticky top-0 z-[1]",
                  col.align === "right" && "text-right",
                )}
              >
                <span
                  className={cn(
                    "inline-flex items-center gap-1",
                    col.align === "right" && "justify-end",
                  )}
                >
                  <HeaderDefinitionTooltip definition={col.definition}>
                    {col.sortable ? (
                      <button
                        type="button"
                        className="inline-flex items-center gap-1 rounded-sm hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                        onClick={() => {
                          if (manual) {
                            manual.onCycle(col.key);
                            return;
                          }
                          setSort((current) => ({
                            key: col.key,
                            direction:
                              current?.key === col.key && current.direction === "asc"
                                ? "desc"
                                : "asc",
                          }));
                        }}
                        aria-label={`Sort by ${col.label}`}
                      >
                        {col.label}
                        {activeSort?.key === col.key ? (
                          activeSort.direction === "asc" ? (
                            <ArrowUpIcon className="size-3" aria-hidden />
                          ) : (
                            <ArrowDownIcon className="size-3" aria-hidden />
                          )
                        ) : (
                          <ArrowUpDownIcon className="size-3 opacity-60" aria-hidden />
                        )}
                      </button>
                    ) : (
                      <span>{col.label}</span>
                    )}
                  </HeaderDefinitionTooltip>
                  {col.definition ? (
                    <DefinitionPopover definition={col.definition} label={col.label} />
                  ) : null}
                </span>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {props.rows.length === 0 ? (
            <tr>
              <td
                className="px-2 py-6 text-sm text-muted-foreground"
                colSpan={props.columns.length}
              >
                {props.emptyMessage || "No rows to display."}
              </td>
            </tr>
          ) : null}
          {sortRows(props.rows).flatMap((row, index) => {
            const key = props.rowKey(row, index);
            const children = props.childRows?.(row);
            return [
              renderRow(row, key, 0, children),
              ...(children?.length && expanded.has(key)
                ? sortRows(children).map((child, childIndex) =>
                    renderRow(child, `${key}/${props.rowKey(child, childIndex)}`, 1),
                  )
                : []),
            ];
          })}
        </tbody>
      </table>
    </div>
  );
}

export function FilterToggle(props: { active: boolean; children: ReactNode; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={props.onClick}
      className={cn(
        "rounded-md border px-2.5 py-1 text-xs font-medium transition-colors",
        props.active
          ? "border-primary bg-primary text-primary-foreground"
          : "border-border bg-background text-muted-foreground hover:bg-accent hover:text-foreground",
      )}
    >
      {props.children}
    </button>
  );
}

/** Full-width dashboard canvas. Chrome owns vertical scroll. */
export function DashboardsPageFrame(
  props: {
    children: ReactNode;
    className?: string;
    /** Fill the chrome pane instead of growing with content (Data Dictionary). */
    fill?: boolean;
    style?: CSSProperties;
  } & Omit<HTMLAttributes<HTMLDivElement>, "children" | "className" | "style">,
) {
  const { children, className, fill, style, ...rest } = props;
  return (
    <div
      className={cn(
        "w-full min-w-0",
        fill ? "flex h-full min-h-0 flex-col px-4 py-4 sm:px-6" : "px-4 py-6 sm:px-6",
        className,
      )}
      style={style}
      {...rest}
    >
      {children}
    </div>
  );
}
